// openEHR BASE model — BMM package org.openehr.base.foundation_types.interval
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { TYPE_REGISTRY } from "../_shared.ts";
import type { T } from "../_shared.ts";
import { Any } from "./foundation_types.ts";
import { Boolean } from "./primitive_types.ts";
import type { Integer, Ordered } from "./primitive_types.ts";

/**
 * Interval abstraction, featuring upper and lower limits that may be open or closed, included or not included.
 */
export abstract class Interval<T extends Ordered> extends Any {
  /**
   * Lower bound.
   */
  lower?: T;
  /**
   * Upper bound.
   */
  upper?: T;
  /**
   * Internal storage for lower_unbounded
   * @protected
   */
  protected _lower_unbounded?: Boolean;

  /**
   * True if \`_lower_\` boundary open (i.e. = \`-infinity\`).
   */
  get lower_unbounded(): boolean | undefined {
    return this._lower_unbounded?.value;
  }

  /**
   * Gets the Boolean wrapper object for lower_unbounded.
   * Use this to access Boolean methods.
   */
  get $lower_unbounded(): Boolean | undefined {
    return this._lower_unbounded;
  }

  /**
   * Sets lower_unbounded from either a primitive value or Boolean wrapper.
   */
  set lower_unbounded(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._lower_unbounded = undefined;
    } else if (typeof val === "boolean") {
      this._lower_unbounded = Boolean.from(val);
    } else {
      this._lower_unbounded = val;
    }
  }

  /**
   * Internal storage for upper_unbounded
   * @protected
   */
  protected _upper_unbounded?: Boolean;

  /**
   * True if \`_upper_\` boundary open (i.e. = \`+infinity\`).
   */
  get upper_unbounded(): boolean | undefined {
    return this._upper_unbounded?.value;
  }

  /**
   * Gets the Boolean wrapper object for upper_unbounded.
   * Use this to access Boolean methods.
   */
  get $upper_unbounded(): Boolean | undefined {
    return this._upper_unbounded;
  }

  /**
   * Sets upper_unbounded from either a primitive value or Boolean wrapper.
   */
  set upper_unbounded(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._upper_unbounded = undefined;
    } else if (typeof val === "boolean") {
      this._upper_unbounded = Boolean.from(val);
    } else {
      this._upper_unbounded = val;
    }
  }

  /**
   * Internal storage for lower_included
   * @protected
   */
  protected _lower_included?: Boolean;

  /**
   * True if \`_lower_\` boundary value included in range, if \`not _lower_unbounded_\`.
   */
  get lower_included(): boolean | undefined {
    return this._lower_included?.value;
  }

  /**
   * Gets the Boolean wrapper object for lower_included.
   * Use this to access Boolean methods.
   */
  get $lower_included(): Boolean | undefined {
    return this._lower_included;
  }

  /**
   * Sets lower_included from either a primitive value or Boolean wrapper.
   */
  set lower_included(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._lower_included = undefined;
    } else if (typeof val === "boolean") {
      this._lower_included = Boolean.from(val);
    } else {
      this._lower_included = val;
    }
  }

  /**
   * Internal storage for upper_included
   * @protected
   */
  protected _upper_included?: Boolean;

  /**
   * True if \`_upper_\` boundary value included in range if \`not _upper_unbounded_\`.
   */
  get upper_included(): boolean | undefined {
    return this._upper_included?.value;
  }

  /**
   * Gets the Boolean wrapper object for upper_included.
   * Use this to access Boolean methods.
   */
  get $upper_included(): Boolean | undefined {
    return this._upper_included;
  }

  /**
   * Sets upper_included from either a primitive value or Boolean wrapper.
   */
  set upper_included(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._upper_included = undefined;
    } else if (typeof val === "boolean") {
      this._upper_included = Boolean.from(val);
    } else {
      this._upper_included = val;
    }
  }

  /**
   * True if the value \`e\` is properly contained in this Interval.
   * @param e - Parameter
   * @returns Result value
   */
  abstract has(e: T): Boolean;

  /**
   * True if there is any overlap between intervals represented by Current and \`_other_\`. True if at least one limit of \`_other_\` is strictly inside the limits of this interval.
   * @param other - Parameter
   * @returns Result value
   */
  abstract intersects(other: Interval<T>): Boolean;

  /**
   * True if current interval properly contains \`_other_\`? True if all points of \`_other_\` are inside the current interval.
   * @param other - Parameter
   * @returns Result value
   */
  abstract contains(other: Interval<T>): Boolean;

  /**
   * True if current object's interval is semantically same as \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (!(other instanceof Interval)) {
      return new Boolean(false);
    }
    
    const otherInterval = other as Interval<T>;
    
    // Compare lower bounds
    if (this.lower_unbounded !== otherInterval.lower_unbounded) {
      return new Boolean(false);
    }
    if (!this.lower_unbounded) {
      if (this.lower === undefined || otherInterval.lower === undefined) {
        return new Boolean(false);
      }
      if (!this.lower.is_equal(otherInterval.lower).value) {
        return new Boolean(false);
      }
      if (this.lower_included !== otherInterval.lower_included) {
        return new Boolean(false);
      }
    }
    
    // Compare upper bounds
    if (this.upper_unbounded !== otherInterval.upper_unbounded) {
      return new Boolean(false);
    }
    if (!this.upper_unbounded) {
      if (this.upper === undefined || otherInterval.upper === undefined) {
        return new Boolean(false);
      }
      if (!this.upper.is_equal(otherInterval.upper).value) {
        return new Boolean(false);
      }
      if (this.upper_included !== otherInterval.upper_included) {
        return new Boolean(false);
      }
    }
    
    return new Boolean(true);
  }
}

/**
 * Type representing a 'proper' Interval, i.e. any two-sided or one-sided interval.
 */
export class Proper_interval<T extends Ordered> extends Interval<T> {
  /**
   * True if the value \`e\` is properly contained in this Interval.
   * @param e - Parameter
   * @returns Result value
   */
  override has(e: T): Boolean {
    // Check lower bound
    if (!this.lower_unbounded && this.lower !== undefined) {
      const cmp = e.less_than(this.lower);
      if (cmp.value === true) return new Boolean(false);
      
      if (!this.lower_included) {
        const eq = e.is_equal(this.lower);
        if (eq.value === true) return new Boolean(false);
      }
    }
    
    // Check upper bound
    if (!this.upper_unbounded && this.upper !== undefined) {
      const cmp = this.upper.less_than(e);
      if (cmp.value === true) return new Boolean(false);
      
      if (!this.upper_included) {
        const eq = e.is_equal(this.upper);
        if (eq.value === true) return new Boolean(false);
      }
    }
    
    return new Boolean(true);
  }

  /**
   * True if there is any overlap between intervals represented by Current and \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override intersects(other: Interval<T>): Boolean {
    // Check if one interval is completely before the other
    if (!this.upper_unbounded && this.upper !== undefined &&
        !other.lower_unbounded && other.lower !== undefined) {
      if (this.upper.less_than(other.lower).value) {
        return new Boolean(false);
      }
      // Check boundary conditions
      if (this.upper.is_equal(other.lower).value &&
          (!this.upper_included || !other.lower_included)) {
        return new Boolean(false);
      }
    }
    
    if (!other.upper_unbounded && other.upper !== undefined &&
        !this.lower_unbounded && this.lower !== undefined) {
      if (other.upper.less_than(this.lower).value) {
        return new Boolean(false);
      }
      // Check boundary conditions
      if (other.upper.is_equal(this.lower).value &&
          (!other.upper_included || !this.lower_included)) {
        return new Boolean(false);
      }
    }
    
    return new Boolean(true);
  }

  /**
   * True if current interval properly contains \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override contains(other: Interval<T>): Boolean {
    // Check lower bound containment
    if (!other.lower_unbounded && other.lower !== undefined) {
      if (this.lower_unbounded) {
        // This is unbounded below, so it contains other's lower
      } else if (this.lower === undefined) {
        return new Boolean(false);
      } else {
        if (this.lower.less_than(other.lower).value) {
          // OK
        } else if (this.lower.is_equal(other.lower).value) {
          // Equal bounds - check inclusion
          if (!this.lower_included && other.lower_included) {
            return new Boolean(false);
          }
        } else {
          return new Boolean(false);
        }
      }
    }
    
    // Check upper bound containment
    if (!other.upper_unbounded && other.upper !== undefined) {
      if (this.upper_unbounded) {
        // This is unbounded above, so it contains other's upper
      } else if (this.upper === undefined) {
        return new Boolean(false);
      } else {
        if (other.upper.less_than(this.upper).value) {
          // OK
        } else if (this.upper.is_equal(other.upper).value) {
          // Equal bounds - check inclusion
          if (!this.upper_included && other.upper_included) {
            return new Boolean(false);
          }
        } else {
          return new Boolean(false);
        }
      }
    }
    
    return new Boolean(true);
  }
}

/**
 * An Interval of Integer, used to represent multiplicity, cardinality and optionality in models.
 */
export class Multiplicity_interval extends Proper_interval<Integer> {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("MULTIPLICITY_INTERVAL", Multiplicity_interval);
  }

  /**
   * True if this interval imposes no constraints, i.e. is set to `0..*`.
   * @returns Result value
   */
  is_open(): Boolean {
    // Check if lower = 0 and upper is unbounded
    const lowerVal = this.lower?.value || 0;
    return new Boolean(
      lowerVal === 0 && this.upper_unbounded === true,
    );
  }

  /**
   * True if this interval expresses optionality, i.e. \`0..1\`.
   * @returns Result value
   */
  is_optional(): Boolean {
    // Check if lower = 0 and upper = 1
    const lowerVal = this.lower?.value || 0;
    const upperVal = this.upper?.value || 0;
    return new Boolean(
      lowerVal === 0 && upperVal === 1,
    );
  }

  /**
   * True if this interval expresses mandation, i.e. \`1..1\`.
   * @returns Result value
   */
  is_mandatory(): Boolean {
    // Check if lower = 1 and upper = 1
    const lowerVal = this.lower?.value || 0;
    const upperVal = this.upper?.value || 0;
    return new Boolean(
      lowerVal === 1 && upperVal === 1,
    );
  }

  /**
   * True if this interval is set to \`0..0\`.
   * @returns Result value
   */
  is_prohibited(): Boolean {
    // Check if lower = 0 and upper = 0
    const lowerVal = this.lower?.value || 0;
    const upperVal = this.upper?.value || 0;
    return new Boolean(
      lowerVal === 0 && upperVal === 0,
    );
  }
}

/**
 * Express constraints on the cardinality of container objects which are the values of multiply-valued attributes, including uniqueness and ordering, providing the means to state that a container acts like a logical list, set or bag.
 */
export class Cardinality {
  /**
   * The interval of this cardinality.
   */
  interval?: Multiplicity_interval;
  /**
   * Internal storage for is_ordered
   * @protected
   */
  protected _is_ordered?: Boolean;

  /**
   * True if the members of the container attribute to which this cardinality refers are ordered.
   */
  get is_ordered(): boolean | undefined {
    return this._is_ordered?.value;
  }

  /**
   * Gets the Boolean wrapper object for is_ordered.
   * Use this to access Boolean methods.
   */
  get $is_ordered(): Boolean | undefined {
    return this._is_ordered;
  }

  /**
   * Sets is_ordered from either a primitive value or Boolean wrapper.
   */
  set is_ordered(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_ordered = undefined;
    } else if (typeof val === "boolean") {
      this._is_ordered = Boolean.from(val);
    } else {
      this._is_ordered = val;
    }
  }

  /**
   * Internal storage for is_unique
   * @protected
   */
  protected _is_unique?: Boolean;

  /**
   * True if the members of the container attribute to which this cardinality refers are unique.
   */
  get is_unique(): boolean | undefined {
    return this._is_unique?.value;
  }

  /**
   * Gets the Boolean wrapper object for is_unique.
   * Use this to access Boolean methods.
   */
  get $is_unique(): Boolean | undefined {
    return this._is_unique;
  }

  /**
   * Sets is_unique from either a primitive value or Boolean wrapper.
   */
  set is_unique(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_unique = undefined;
    } else if (typeof val === "boolean") {
      this._is_unique = Boolean.from(val);
    } else {
      this._is_unique = val;
    }
  }

  /**
   * True if the semantics of this cardinality represent a bag, i.e. unordered, non-unique membership.
   * @returns Result value
   */
  is_bag(): Boolean {
    // Bag: unordered and non-unique
    return new Boolean(
      this.is_ordered !== true && this.is_unique !== true,
    );
  }

  /**
   * True if the semantics of this cardinality represent a list, i.e. ordered, non-unique membership.
   * @returns Result value
   */
  is_list(): Boolean {
    // List: ordered and non-unique
    return new Boolean(
      this.is_ordered === true && this.is_unique !== true,
    );
  }

  /**
   * True if the semantics of this cardinality represent a set, i.e. unordered, unique membership.
   * @returns Result value
   */
  is_set(): Boolean {
    // Set: unordered and unique
    return new Boolean(
      this.is_ordered !== true && this.is_unique === true,
    );
  }
}

/**
 * Type representing an Interval that happens to be a point value. Provides an efficient representation that is substitutable for \`Interval<T>\` where needed.
 */
export class Point_interval<T extends Ordered> extends Interval<T> {
  /**
   * Lower boundary open (i.e. = -infinity).
   */
  override get lower_unbounded(): boolean | undefined {
    return this._lower_unbounded?.value;
  }

  /**
   * Gets the Boolean wrapper object for lower_unbounded.
   * Use this to access Boolean methods.
   */
  override get $lower_unbounded(): Boolean | undefined {
    return this._lower_unbounded;
  }

  /**
   * Sets lower_unbounded from either a primitive value or Boolean wrapper.
   */
  override set lower_unbounded(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._lower_unbounded = undefined;
    } else if (typeof val === "boolean") {
      this._lower_unbounded = Boolean.from(val);
    } else {
      this._lower_unbounded = val;
    }
  }

  /**
   * Upper boundary open (i.e. = +infinity).
   */
  override get upper_unbounded(): boolean | undefined {
    return this._upper_unbounded?.value;
  }

  /**
   * Gets the Boolean wrapper object for upper_unbounded.
   * Use this to access Boolean methods.
   */
  override get $upper_unbounded(): Boolean | undefined {
    return this._upper_unbounded;
  }

  /**
   * Sets upper_unbounded from either a primitive value or Boolean wrapper.
   */
  override set upper_unbounded(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._upper_unbounded = undefined;
    } else if (typeof val === "boolean") {
      this._upper_unbounded = Boolean.from(val);
    } else {
      this._upper_unbounded = val;
    }
  }

  /**
   * Lower boundary value included in range if not \`_lower_unbounded_\`.
   */
  override get lower_included(): boolean | undefined {
    return this._lower_included?.value;
  }

  /**
   * Gets the Boolean wrapper object for lower_included.
   * Use this to access Boolean methods.
   */
  override get $lower_included(): Boolean | undefined {
    return this._lower_included;
  }

  /**
   * Sets lower_included from either a primitive value or Boolean wrapper.
   */
  override set lower_included(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._lower_included = undefined;
    } else if (typeof val === "boolean") {
      this._lower_included = Boolean.from(val);
    } else {
      this._lower_included = val;
    }
  }

  /**
   * Upper boundary value included in range if not \`_upper_unbounded_\`.
   */
  override get upper_included(): boolean | undefined {
    return this._upper_included?.value;
  }

  /**
   * Gets the Boolean wrapper object for upper_included.
   * Use this to access Boolean methods.
   */
  override get $upper_included(): Boolean | undefined {
    return this._upper_included;
  }

  /**
   * Sets upper_included from either a primitive value or Boolean wrapper.
   */
  override set upper_included(val: boolean | Boolean | undefined) {
    if (val === undefined || val === null) {
      this._upper_included = undefined;
    } else if (typeof val === "boolean") {
      this._upper_included = Boolean.from(val);
    } else {
      this._upper_included = val;
    }
  }

  /**
   * True if the value \`v\` equals this point value.
   * @param v - Parameter
   * @returns Result value
   */
  override has(v: T): Boolean {
    if (this.lower === undefined) {
      return new Boolean(false);
    }
    return this.lower.is_equal(v);
  }

  /**
   * A point interval never intersects with any other interval (it's a single point).
   * @param other - Parameter
   * @returns Result value
   */
  override intersects(other: Interval<T>): Boolean {
    if (this.lower === undefined) {
      return new Boolean(false);
    }
    // A point intersects if the other interval contains this point
    return other.has(this.lower);
  }

  /**
   * A point interval can only contain another interval if that interval is also the same point.
   * @param other - Parameter
   * @returns Result value
   */
  override contains(other: Interval<T>): Boolean {
    if (this.lower === undefined) {
      return new Boolean(false);
    }
    // Only contains if other is a point at the same location
    if (other instanceof Point_interval) {
      return this.is_equal(other);
    }
    // Cannot contain a proper interval
    return new Boolean(false);
  }
}
